/* eslint-disable react-hooks/exhaustive-deps */
import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { set } from 'lodash';
import { useTranslation } from 'react-i18next';
import * as action from '../../store/DevelopmentOrder.action';
import * as systemMasterAction from '../../../SystemMasters/Store/SystemMasters.action';
import * as productListAction from '../../../RangePlan/ProductList/store/ProductList.action';
import { createOptions, monthsPlaceholder, getYears, checkArray } from '../../../../Components/helper';
import { fetchMaster } from '../../../../store/master-registry';

/**
 * Shared product-information state. The Asset Library provides only `filterState` and
 * `setFilterState` to reuse ProductFilter, so TypeScript callers see the rest as optional.
 * @type {import('react').Context<{ filterState: Record<string, unknown>, setFilterState: (...args: never[]) => void, state?: Record<string, unknown>, setState?: (updater: (prev: Record<string, unknown>) => Record<string, unknown>) => void, callGetDevorderListApi?: (params: Record<string, unknown>) => void, hasMore?: boolean, cardData?: unknown, setCardData?: (...args: never[]) => void, userRole?: string } | null>}
 */
export const StateContext = createContext(null);

export default function ProductInfoStateProvider({ children }) {
  const dispatch = useDispatch();
  const location = useLocation();
  const isNewProductRoute = /\/create$/.test(location?.pathname || '');
  const { t } = useTranslation('common');
  const reducer = useSelector((reducerState) => reducerState);
  const {
    UserDetails,
    Measuremets,
    Users: { logInRights },
  } = reducer;
  const { userInfo, userRole } = UserDetails;
  const { devOrderList, totalProductCount } = Measuremets;
  const initialSetting = {
    expandCard: '',
    pageIndex: 0,
    productTotalCount: 0,
    productData: [],
    hasMoreData: false,
    devOrderId: null,
    searchText: '',
    filterData: null,
    filterSort: null,
    activeList: 'open',
    productGroupOption: [],
    productStatusOptions: [],
    selectedIds: [],
    isSelectAll: false,
    isArchivedModal: false,
    isTrashModal: false,
    pimStatusConfirm: null,
    archivedComment: '',
    isCompletionModalOpen: false,
    completionData: null,
    currentProduct: null,
    isProductGroupModalOpen: false,
    isDeleteOpen: false,
    deletedDevOrderId: null,
    loading: false,
    selectProductTag: 'product',
    tagsOption: [],
    activeTags: [],
    isAddProductGroup: false,
    newProductGroup: '',
    openTechPackState: logInRights?.[0]?.openTechPack,
    deleteTechPackState: logInRights?.[0]?.deleteTechPack,
    archieveTechPackState: logInRights?.[0]?.archieveTechPack,
    trashTechPackState: logInRights?.[0]?.trashTechPack,
    pageLoading: true,
    selectedGroupId: null,
    scrollLoader: false,
    groupError: false,
    isLinkOpen: false,
    LinkId: '',
    selectedGroup: null,
    loader: false,
  };

  const initialFilterSetting = {
    brandOptions: [],
    category1Options: [],
    category2Options: [],
    category3Options: [],
    category4Options: [],
    productTypeOptions: [],
    productStatusOptions: [],
    seasonOptions: [],
    supplierOptions: [],
    createdByOption: [],
    monthOptions: createOptions(monthsPlaceholder, 'name', '_id'),
    yearOptions: createOptions(getYears(), 'name', '_id'),
    activeBrand: [],
    activeProductType: [],
    activeCat1: [],
    activeCat2: [],
    activeCat3: [],
    activeCat4: [],
    activeSupplier: [],
    activeSeason: [],
    activeMonth: [],
    activeYear: [],
    activeStatus: [],
    activeCreatedBy: [],
    dynamicFields: [],
    dynamicTypes: [],
    textCustomAttribute: [],
    productIdentifiers: [],
    activeColorway: [],
  };

  const initialCardSetting = {
    expandCard: '',
    selectedClusterIds: {},
  };
  const [state, setState] = useState(initialSetting);
  const [filterState, setFilterState] = useState(initialFilterSetting);
  const [cardData, setCardData] = useState(initialCardSetting);

  /**
   * Single entry point for the product-list API. `activeTags` is a page-level search term in
   * the same way `searchText` is, but it is not threaded through the ~20 call sites that
   * refetch this list (apply filter, sort, change group, change status, archive, scroll, …),
   * so every one of them used to silently drop the tag filter. Injecting it here keeps it
   * applied everywhere; a caller that needs to change or clear it passes `searchedTags`
   * explicitly, and an explicit value always wins.
   * @param {Record<string, unknown>} params
   * @returns {Promise<unknown>}
   */
  const callGetDevorderListApi = async (params) => {
    const payload = params?.searchedTags === undefined && checkArray(state?.activeTags) ? { ...params, searchedTags: state.activeTags } : params;
    const res = await dispatch(action.getDevelopmentOrder(payload));
    setState((prevData) => ({
      ...prevData,
      pageLoading: false,
      scrollLoader: false,
    }));
    return res;
  };

  useEffect(() => {
    const callFn = async () => {
      const localstorageFilter = JSON.parse(localStorage?.getItem('productListFilter'));
      const cloneState = state;
      const listApiObj = { archived: state?.activeList !== 'open', searchText: state?.searchText, count: 0, ...localstorageFilter, ...state?.filterSort };
      set(cloneState, 'pageLoading', true);
      if (localstorageFilter) {
        set(cloneState, 'filterData', localstorageFilter);
        listApiObj.deleted = state?.activeList === 'bin';
      }
      setState((prevData) => ({
        ...prevData,
        ...cloneState,
      }));
      const res = await callGetDevorderListApi(listApiObj);
      if (res) {
        getProductGroupsStatus();
      }
    };
    callFn();
  }, []);

  useEffect(() => {
    if (!location?.state?.refreshList) return;
    const localstorageFilter = JSON.parse(localStorage?.getItem('productListFilter'));
    setState((prevData) => ({ ...prevData, pageIndex: 0, pageLoading: true, productData: [] }));
    callGetDevorderListApi({
      archived: state?.activeList !== 'open',
      count: 0,
      searchText: state?.searchText,
      deleted: state?.activeList === 'bin',
      ...localstorageFilter,
      ...state?.filterSort,
    });
    window.history.replaceState({}, document.title);
  }, [location?.state?.refreshList]);

  const getProductGroupsStatus = () => {
    try {
      Promise.all([
        dispatch(systemMasterAction.fetchProductIdentifier(t)),
        dispatch(systemMasterAction.fetchProductIdentifierType(t)),
        dispatch(productListAction.fetchProductGroup(t)),
        dispatch(fetchMaster('productStatus', { loggedInUserId: userInfo?.userId, isActive: 1 })),
        dispatch(fetchMaster('customAttributeFreeText')),
        dispatch(fetchMaster('pcTypes', { loggedInUserId: userInfo?.userId, isActive: true, tier: 'Tier1' })),
        dispatch(fetchMaster('seasons', { loggedInUserId: userInfo?.userId })),
        dispatch(fetchMaster('filterSeasons', { loggedInUserId: userInfo?.userId })),
        dispatch(fetchMaster('brands', { loggedInUserId: userInfo?.userId, isActive: 1 })),
        dispatch(fetchMaster('category1', { categoryType: 1, loggedInUserId: userInfo?.userId, isActive: true })),
        dispatch(fetchMaster('category2', { categoryType: 2, loggedInUserId: userInfo?.userId, isActive: true })),
        /*
         * Shares the `ReferenceSupplier` slice with CreateProduct, so it has to agree
         * with that screen about which suppliers to load — otherwise this call lands
         * second and replaces the active-only list with the full one. Creating a new
         * product narrows to active suppliers; editing keeps everything so a product
         * already referencing a deactivated supplier still renders it. Forced so a
         * cache hit never leaves the other variant's list in the slice.
         */
        dispatch(fetchMaster('referenceSupplier', isNewProductRoute ? { activeOnly: true } : undefined, { force: true })),
        dispatch(action.getAdminUser()),
        dispatch(action.getCompanyUser({ CompanyUsers: true })),
      ]);
    } catch (err) {
      console.log('Error', err);
    }
  };

  const filterDevOrderListIfIdAndParentdevIdAlreaduExist = (devOrderList) => {
    // Set to keep track of seen _id
    const seenIds = new Set();

    // Filter out duplicates based on the condition
    const uniqueArray = devOrderList.filter((obj) => {
      // If _id is already seen, skip this object
      if (seenIds.has(obj._id)) {
        return false;
      }
      // Otherwise, mark the current _id as seen and keep this object
      seenIds.add(obj._id);
      return true;
    });
    return uniqueArray || [];
  };

  useEffect(() => {
    if (Array.isArray(devOrderList)) {
      setState((prev) => ({
        ...prev,
        productData: filterDevOrderListIfIdAndParentdevIdAlreaduExist(devOrderList),
      }));
    }
  }, [devOrderList, totalProductCount]);

  useEffect(() => {
    if (state?.isSelectAll && !state?.isLinkOpen) {
      if (checkArray(state?.productData)) {
        setState((prevData) => ({
          ...prevData,
          isSelectAll: state?.isSelectAll,
          selectedIds: state?.productData?.map((ele) => {
            return ele?._id;
          }),
        }));
      }
    }
  }, [state?.productData?.length, state?.isSelectAll, state?.isLinkOpen]);

  const hasMore = useMemo(() => {
    if (checkArray(devOrderList)) return totalProductCount > devOrderList?.length;
    return false;
  }, [devOrderList?.length, totalProductCount]);

  const contextValue = useMemo(
    () => ({
      state,
      setState,
      callGetDevorderListApi,
      setFilterState,
      filterState,
      hasMore,
      setCardData,
      cardData,
      userRole,
    }),
    [state, setState, callGetDevorderListApi, setFilterState, filterState, hasMore, setCardData, cardData, userRole],
  );
  return (
    <StateContext.Provider
      value={{
        ...contextValue,
      }}
    >
      {children}
    </StateContext.Provider>
  );
}

export function useProductInformationState() {
  const { t } = useTranslation('common');
  const context = useContext(StateContext);
  if (!context) {
    throw new Error(t('FILTER.APP_STATE_MESSAGE'));
  }
  return context;
}
